import React, { useContext, useEffect, useMemo, useState } from 'react';
import pt from 'prop-types';
import Headline, { headlinePropTypes } from '../../ui/headline';
import AccordionItem, {
  accordionItemPropTypes,
} from './components/accordionItem';

import * as styles from './styles.module.scss';
import Container from '../../../modules/container';
import RichText from '../../ui/richText';
import Link from '../../ui/link';
import { combine } from '../../../helpers/styles';
import SearchField from './components/searchField';
import { getCopy } from '../../../helpers/copy';
import { MicrocopyContext } from '../../../context/microcopy.context';
import { getContentAsText } from '../../../helpers/data';

const CMSAccordion = ({
  headline,
  subline,
  content,
  cta,
  beigeBackground,
  inContainer,
  isSearchable,
  defaultOpenIndex,
  smallItemHeadlines,
}) => {
  const [openIndex, setOpenIndex] = useState(defaultOpenIndex);
  const [searchTerm, setSearchTerm] = useState('');
  const [autoSuggestion, setAutoSuggestion] = useState([]);
  const microcopy = useContext(MicrocopyContext);

  const contentAsText = useMemo(() => {
    const items = content.map((item, index) => {
      return {
        index,
        headline: item.headline,
        content: getContentAsText(item.content).filter(Boolean),
      };
    });
    return items;
  }, [content]);

  const handleClick = (index) => {
    const newOpenIndex = openIndex === index ? null : index;
    setOpenIndex(newOpenIndex);
  };

  useEffect(() => {
    if (searchTerm.length >= 3) {
      const suggestions = [];
      contentAsText.forEach((item) => {
        if (item.headline.toLowerCase().includes(searchTerm.toLowerCase())) {
          suggestions.push({
            index: item.index,
            headline: item.headline,
            content: '',
          });
        } else {
          const matchingContent = item.content.find((content) =>
            content.toLowerCase().includes(searchTerm.toLowerCase())
          );
          if (matchingContent) {
            suggestions.push({
              index: item.index,
              headline: item.headline,
              content: matchingContent,
            });
          }
        }
      });
      setAutoSuggestion(suggestions);
    } else {
      setAutoSuggestion([]);
    }
  }, [searchTerm]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleReset = () => {
    setSearchTerm('');
  };

  return (
    <div
      className={combine(
        !inContainer && styles.outerContainer,
        beigeBackground && styles.beige
      )}
    >
      <Container className={styles.innerContainer}>
        {isSearchable && (
          <SearchField
            className={styles.searchField}
            onChange={handleSearchChange}
            onReset={handleReset}
            searchTerm={searchTerm}
            autoSuggestion={autoSuggestion}
            onItemClick={setOpenIndex}
            onBackdropClick={handleReset}
            small
            placeholder={getCopy('searchField.placeholder', microcopy)}
          />
        )}
        {!inContainer && <Headline type="h1" level="h2" text={headline} />}
        {subline && (
          <RichText
            content={subline}
            paragraphAs="p"
            paragraphType="p1"
            className={styles.subline}
          />
        )}
        {content?.map((item, index) => (
          <AccordionItem
            key={`accordion-item-${index}`}
            id={`accordion-item-${index}`}
            headline={item.headline}
            content={item.content}
            isFirst={index === 0}
            isOpen={index === openIndex}
            onOpen={() => handleClick(index)}
            isSmallHeadline={smallItemHeadlines}
          />
        ))}
        {cta && (
          <Link
            key={cta.text}
            link={cta}
            asButton
            allowIcon
            className={styles.cta}
          >
            {cta.text}
          </Link>
        )}
      </Container>
    </div>
  );
};

CMSAccordion.propTypes = {
  headline: pt.shape(headlinePropTypes).isRequired,
  subline: pt.shape(headlinePropTypes),
  content: pt.shape(accordionItemPropTypes),
};

CMSAccordion.defaultProps = {
  content: [],
};

export default CMSAccordion;
